/**
 * Why the AI proposed this juurisyy (specs/systemic-map-v2-room-review.md §1–2):
 * the mechanism, what it rests on, the sources it cites, and what would make it
 * false — so the room judges the reasoning, not only the sentence.
 * Cards derived before this existed have no rationale; they show nothing here.
 *
 * `verification` is the research check's result. When the card was not checked,
 * the card says so rather than looking checked: a wording the room refined has
 * no sources of its own yet, and a check that failed is not a pass
 * (specs/systemic-v2-grounded-generation.md G5). `ownReasoning` is why a card
 * the check kept without a source is deliberately the analyst's reasoning.
 *
 * `large` (the Juurisyyt card, #1079) sets the text at reading size.
 *
 * The why stays in view; what would make it false and the deliberate reasoning
 * fold under one line, and the sources under theirs (#1194).
 */
import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation';
import SourceList from './SourceList';
import Disclosure from '../../components/ui/Disclosure';

const Box = styled.div`margin-top: 12px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 10px;`;
const Label = styled.p`margin: 0 0 6px; font-size: 12px; letter-spacing: 0.04em; text-transform: uppercase; color: rgba(255,255,255,0.55);`;
const Text = styled.p`margin: 4px 0; font-size: ${(p) => (p.$large ? '16px' : '14px')}; line-height: 1.5; color: rgba(255,255,255,0.88);`;
const Basis = styled.span`
  display: inline-block; padding: 0 8px; border-radius: 999px; font-size: 12px; font-weight: 600;
  background: ${(p) => ({ research: 'rgba(125,211,160,0.18)', analogue: 'rgba(158,203,255,0.18)', reasoned: 'rgba(255,255,255,0.1)' }[p.$b])};
`;

const Note = styled.p`margin: 6px 0 0; font-size: 13px; line-height: 1.4; color: #ffc46b;`;

export default function CardWhy({ rationale, verification, large = false }) {
  const { t } = useTranslation();
  const hasRationale = Boolean(rationale && (rationale.why || rationale.falsifier || rationale.ownReasoning || rationale.sources?.length));
  const unverified = verification?.action === 'unverified';
  if (!hasRationale && !unverified) return null;
  return (
    <Box>
      <Label>
        {t('juurisyyV2.why.title')}
        {hasRationale ? <> <Basis $b={rationale.basis}>{t(`juurisyyV2.why.basis.${rationale.basis || 'reasoned'}`)}</Basis></> : null}
      </Label>
      {hasRationale && rationale.why ? <Text $large={large}>{rationale.why}</Text> : null}
      {hasRationale && (rationale.falsifier || rationale.ownReasoning) ? (
        <Disclosure variant="inline" summary={t('juurisyyV2.why.more')}>
          {rationale.falsifier ? <Text $large={large}><strong>{t('juurisyyV2.why.falsifier')}</strong> {rationale.falsifier}</Text> : null}
          {rationale.ownReasoning ? <Text $large={large}><strong>{t('juurisyyV2.why.ownReasoning')}</strong> {rationale.ownReasoning}</Text> : null}
        </Disclosure>
      ) : null}
      {hasRationale ? <SourceList sources={rationale.sources || []} unavailable={rationale.sourcesUnavailable} showNone /> : null}
      {unverified ? (
        <Note>{t(verification.reason === 'refined_by_room' ? 'juurisyyV2.why.unverifiedRefined' : 'juurisyyV2.why.unverified')}</Note>
      ) : null}
    </Box>
  );
}
